/** Turning a video (file, URL, or webcam) into stacked layers of the volume. */

import { yieldToEventLoop } from './async';
import type { SourceSettings } from './state';
import type { Volume } from './volume';

type RVFCVideo = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, metadata: { mediaTime: number }) => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

export interface SourceInfo {
  kind: 'file' | 'url' | 'webcam' | 'synthetic';
  name: string;
  width: number;
  height: number;
  /** Seconds. 0 for live sources. */
  duration: number;
  frameRate: number;
}

export interface IngestProgress {
  done: number;
  total: number;
  label: string;
}

export class CancelledError extends Error {
  constructor() {
    super('cancelled');
    this.name = 'CancelledError';
  }
}

export interface CaptureHandlers {
  onProgress?: (progress: IngestProgress) => void;
  /** Called after each layer lands, with its logical index. */
  onLayer?: (logicalLayer: number) => void;
}

/** Give up waiting for new frames after this long without progress. */
const STALL_TIMEOUT_MS = 2500;

function waitForEvent(target: EventTarget, event: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    let timer = 0;
    const cleanup = () => {
      target.removeEventListener(event, onDone);
      target.removeEventListener('error', onError);
      if (timer) window.clearTimeout(timer);
    };
    const onDone = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`"${event}" failed`));
    };
    target.addEventListener(event, onDone, { once: true });
    target.addEventListener('error', onError, { once: true });
    if (timeoutMs > 0) {
      timer = window.setTimeout(() => {
        cleanup();
        resolve();
      }, timeoutMs);
    }
  });
}

export class Ingest {
  private video: RVFCVideo | null = null;
  private objectUrl: string | null = null;
  private stream: MediaStream | null = null;

  /**
   * Every capture or live loop remembers the run it belongs to and stops the moment
   * a newer one starts. Without this, an abandoned frame callback keeps writing into
   * the volume behind the new one's back.
   */
  private runId = 0;
  private cancelledRun = -1;

  private frameHandle = 0;
  private rafHandle = 0;

  private signatureCanvas: HTMLCanvasElement;
  private signatureCtx: CanvasRenderingContext2D;
  private lastSignature: Uint8ClampedArray | null = null;

  info: SourceInfo | null = null;

  constructor() {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 18;
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
    if (!ctx) throw new Error('Could not create the signature context');
    this.signatureCanvas = canvas;
    this.signatureCtx = ctx;
  }

  get element(): HTMLVideoElement | null {
    return this.video;
  }

  get isLive(): boolean {
    return this.liveRunId === this.runId && this.liveRunId > 0;
  }

  private liveRunId = 0;

  /** Stops whatever loop is running and starts a new run generation. */
  private beginRun(): number {
    this.stopLoops();
    this.runId++;
    return this.runId;
  }

  private stopLoops(): void {
    if (this.frameHandle && this.video?.cancelVideoFrameCallback) {
      this.video.cancelVideoFrameCallback(this.frameHandle);
    }
    this.frameHandle = 0;
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = 0;
    }
  }

  cancel(): void {
    this.cancelledRun = this.runId;
    this.stopLoops();
  }

  private isStale(run: number): boolean {
    return run !== this.runId || this.cancelledRun === run;
  }

  private createVideoElement(): RVFCVideo {
    const video = document.createElement('video') as RVFCVideo;
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.loop = false;
    return video;
  }

  private releaseSource(): void {
    this.beginRun();
    if (this.video) {
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.srcObject = null;
      this.video.load();
      this.video = null;
    }
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
    this.lastSignature = null;
    this.liveRunId = 0;
  }

  async loadFile(file: File): Promise<SourceInfo> {
    this.releaseSource();
    const url = URL.createObjectURL(file);
    this.objectUrl = url;
    return this.openVideoUrl(url, file.name, 'file');
  }

  async loadUrl(url: string): Promise<SourceInfo> {
    this.releaseSource();
    return this.openVideoUrl(url, url.split('/').pop() || url, 'url');
  }

  private async openVideoUrl(url: string, name: string, kind: 'file' | 'url'): Promise<SourceInfo> {
    const video = this.createVideoElement();
    this.video = video;
    video.src = url;

    await new Promise<void>((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error(`Could not decode "${name}". Try an MP4 (H.264) or WebM file.`));
      };
      const cleanup = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('error', onError);
      };
      video.addEventListener('loadeddata', onReady, { once: true });
      video.addEventListener('error', onError, { once: true });
      video.load();
    });

    if (!video.videoWidth || !video.videoHeight) {
      throw new Error(`"${name}" reported no video dimensions.`);
    }

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    this.info = {
      kind,
      name,
      width: video.videoWidth,
      height: video.videoHeight,
      duration,
      frameRate: 30,
    };
    return this.info;
  }

  async startWebcam(): Promise<SourceInfo> {
    this.releaseSource();
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('This browser exposes no camera API.');
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
      audio: false,
    });
    this.stream = stream;

    const video = this.createVideoElement();
    this.video = video;
    video.srcObject = stream;
    await video.play();
    if (!video.videoWidth) {
      await waitForEvent(video, 'loadeddata', 4000);
    }

    const track = stream.getVideoTracks()[0];
    const trackSettings = track?.getSettings() ?? {};
    this.info = {
      kind: 'webcam',
      name: track?.label || 'webcam',
      width: video.videoWidth || trackSettings.width || 1280,
      height: video.videoHeight || trackSettings.height || 720,
      duration: 0,
      frameRate: trackSettings.frameRate ?? 30,
    };
    return this.info;
  }

  /** Cheap perceptual signature used to skip near-identical frames. */
  private isDuplicate(source: HTMLVideoElement, tolerance = 2.0): boolean {
    const ctx = this.signatureCtx;
    const { width, height } = this.signatureCanvas;
    ctx.drawImage(source, 0, 0, width, height);
    const data = ctx.getImageData(0, 0, width, height).data;
    const prev = this.lastSignature;
    this.lastSignature = new Uint8ClampedArray(data);
    if (!prev || prev.length !== data.length) return false;

    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += Math.abs(data[i] - prev[i]) + Math.abs(data[i + 1] - prev[i + 1]) + Math.abs(data[i + 2] - prev[i + 2]);
    }
    const mean = sum / ((data.length / 4) * 3);
    return mean < tolerance;
  }

  /** Fills the volume from the loaded clip. Resolves once capture stops. */
  async captureAll(volume: Volume, source: SourceSettings, handlers: CaptureHandlers = {}): Promise<void> {
    const video = this.video;
    if (!video) throw new Error('No video loaded.');

    const run = this.beginRun();
    this.cancelledRun = -1;
    this.lastSignature = null;
    volume.clear();

    const total = volume.layers;
    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    if (this.resolveCaptureMode(source.captureMode, duration) === 'even') {
      await this.captureEvenly(run, video, volume, total, duration, handlers);
    } else {
      await this.captureSequentially(run, video, volume, source, total, duration, handlers);
    }
  }

  /**
   * Seeking is preferred whenever the clip length is known: short forward seeks only
   * decode a frame or two, and every layer lands on an exact, evenly spaced timestamp.
   * Playing the clip through is subject to dropped frames, which would leave the t
   * axis unevenly sampled, so it is only the fallback for sources of unknown length.
   */
  private resolveCaptureMode(mode: SourceSettings['captureMode'], duration: number): 'even' | 'sequential' {
    if (mode !== 'auto') return duration > 0 ? mode : 'sequential';
    return duration > 0 ? 'even' : 'sequential';
  }

  private async captureEvenly(
    run: number,
    video: RVFCVideo,
    volume: Volume,
    total: number,
    duration: number,
    handlers: CaptureHandlers,
  ): Promise<void> {
    video.pause();
    const step = duration / total;
    let lastUiUpdate = 0;

    for (let i = 0; i < total; i++) {
      if (this.isStale(run)) throw new CancelledError();
      const time = Math.min(duration - 1e-3, (i + 0.5) * step);
      if (Math.abs(video.currentTime - time) > 1e-4) {
        video.currentTime = time;
        await waitForEvent(video, 'seeked', 2500);
      }
      if (this.isStale(run)) throw new CancelledError();

      const layer = volume.push(video, time);
      handlers.onLayer?.(layer);

      const now = performance.now();
      if (now - lastUiUpdate > 60 || i === total - 1) {
        lastUiUpdate = now;
        handlers.onProgress?.({ done: i + 1, total, label: 'seeking through clip' });
      }
      await yieldToEventLoop();
    }

    if (this.info) {
      this.info.frameRate = total / duration;
    }
  }

  private async captureSequentially(
    run: number,
    video: RVFCVideo,
    volume: Volume,
    source: SourceSettings,
    total: number,
    duration: number,
    handlers: CaptureHandlers,
  ): Promise<void> {
    video.currentTime = 0;
    video.playbackRate = 1;
    video.loop = false;
    await video.play().catch(() => undefined);
    if (this.isStale(run)) throw new CancelledError();

    // With no explicit gap, spread the captures over the whole clip so a clip with
    // more frames than layers still reaches its final frame.
    const interval = source.captureInterval > 0 ? source.captureInterval : duration > 0 ? duration / total : 0;
    const mediaTimes: number[] = [];
    let lastCapturedTime = -Infinity;

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let watchdog = 0;
      let lastProgressAt = performance.now();
      let lastCount = volume.count;

      const cleanup = () => {
        video.removeEventListener('ended', onEnded);
        if (watchdog) window.clearInterval(watchdog);
        this.stopLoops();
      };
      const finish = () => {
        if (settled) return;
        settled = true;
        cleanup();
        video.pause();
        resolve();
      };
      const fail = (error: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        video.pause();
        reject(error);
      };
      const onEnded = () => finish();

      /** Returns false once capture should stop. */
      const onFrame = (mediaTime: number): boolean => {
        if (this.isStale(run)) {
          fail(new CancelledError());
          return false;
        }
        if (interval > 0 && mediaTime - lastCapturedTime < interval) return true;
        if (source.skipDuplicates && this.isDuplicate(video)) return true;

        lastCapturedTime = mediaTime;
        mediaTimes.push(mediaTime);
        const layer = volume.push(video, mediaTime);
        handlers.onLayer?.(layer);
        handlers.onProgress?.({ done: volume.count, total, label: 'decoding clip' });
        return volume.count < total;
      };

      // Frame callbacks stop arriving when playback ends or stalls, so progress is
      // also watched on a timer; otherwise a short clip would never settle.
      watchdog = window.setInterval(() => {
        if (settled) return;
        if (this.isStale(run)) {
          fail(new CancelledError());
          return;
        }
        if (volume.count !== lastCount) {
          lastCount = volume.count;
          lastProgressAt = performance.now();
          return;
        }
        if (video.ended || performance.now() - lastProgressAt > STALL_TIMEOUT_MS) {
          finish();
        }
      }, 250);

      video.addEventListener('ended', onEnded);

      if (typeof video.requestVideoFrameCallback === 'function') {
        const tick = (_now: number, metadata: { mediaTime: number }) => {
          if (settled) return;
          if (!onFrame(metadata.mediaTime)) {
            finish();
            return;
          }
          this.frameHandle = video.requestVideoFrameCallback?.(tick) ?? 0;
        };
        this.frameHandle = video.requestVideoFrameCallback(tick);
      } else {
        const tick = () => {
          if (settled) return;
          if (!onFrame(video.currentTime)) {
            finish();
            return;
          }
          this.rafHandle = requestAnimationFrame(tick);
        };
        this.rafHandle = requestAnimationFrame(tick);
      }
    });

    if (this.info && mediaTimes.length > 2) {
      const deltas: number[] = [];
      for (let i = 1; i < mediaTimes.length; i++) {
        const d = mediaTimes[i] - mediaTimes[i - 1];
        if (d > 1e-4) deltas.push(d);
      }
      if (deltas.length) {
        deltas.sort((a, b) => a - b);
        const median = deltas[Math.floor(deltas.length / 2)];
        this.info.frameRate = 1 / median;
      }
    }
  }

  /** Continuously appends frames to the ring buffer (webcam / looping clip). */
  startLive(volume: Volume, source: SourceSettings, onLayer: (layer: number) => void): void {
    const video = this.video;
    if (!video) throw new Error('No video loaded.');

    const run = this.beginRun();
    this.cancelledRun = -1;
    this.liveRunId = run;

    const interval = Math.max(0, source.captureInterval);
    let lastCapturedTime = -Infinity;

    const capture = (mediaTime: number) => {
      if (interval > 0 && mediaTime - lastCapturedTime < interval) return;
      if (source.skipDuplicates && this.isDuplicate(video)) return;
      lastCapturedTime = mediaTime;
      onLayer(volume.push(video, mediaTime));
    };

    if (typeof video.requestVideoFrameCallback === 'function') {
      const tick = (_now: number, metadata: { mediaTime: number }) => {
        if (this.isStale(run)) return;
        capture(metadata.mediaTime);
        this.frameHandle = video.requestVideoFrameCallback?.(tick) ?? 0;
      };
      this.frameHandle = video.requestVideoFrameCallback(tick);
    } else {
      const tick = () => {
        if (this.isStale(run)) return;
        capture(video.currentTime);
        this.rafHandle = requestAnimationFrame(tick);
      };
      this.rafHandle = requestAnimationFrame(tick);
    }

    if (!video.srcObject) {
      video.loop = true;
      if (video.paused) void video.play().catch(() => undefined);
    }
  }

  stopLive(): void {
    if (this.liveRunId) {
      this.beginRun();
      this.liveRunId = 0;
    }
  }

  dispose(): void {
    this.releaseSource();
    this.info = null;
  }
}
